import { useState } from "react";
import { motion } from "framer-motion";
import { Brain, Wand2, Palette, Music } from "lucide-react";
import { Dialog, DialogContent } from "../ui/dialog";
import { SectionHeading } from "./SectionHeading";
import { fadeUp, stagger, viewportOnce } from "../../lib/motion";
import { MemoryMatch } from "../games/MemoryMatch";
import { TalentQuiz } from "../games/TalentQuiz";
import { ColoringCanvas } from "../games/ColoringCanvas";
import { RhythmTap } from "../games/RhythmTap";
import { useLang } from "../../i18n/LanguageContext";

const ICONS = { memory: Brain, quiz: Wand2, canvas: Palette, rhythm: Music };
const COMPS = { memory: MemoryMatch, quiz: TalentQuiz, canvas: ColoringCanvas, rhythm: RhythmTap };

export const KidsGames = () => {
  const { t } = useLang();
  const s = t.games;
  const [open, setOpen] = useState(null);
  const current = s.cards.find((g) => g.id === open);
  const CurrentComp = current ? COMPS[current.id] : null;
  const CurrentIcon = current ? ICONS[current.id] : null;

  return (
    <section id="games" data-testid="games-section" className="relative overflow-hidden bg-ink-900 py-24 md:py-32">
      <div className="pointer-events-none absolute left-1/2 top-10 h-[360px] w-[360px] -translate-x-1/2 rounded-full bg-gold-primary/10 blur-[130px]" />
      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-8">
        <SectionHeading eyebrow={s.eyebrow} title={s.title} accent={s.accent} subtitle={s.subtitle} testid="games-heading" />

        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={viewportOnce} className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {s.cards.map((g) => {
            const Icon = ICONS[g.id];
            return (
              <motion.button
                key={g.id}
                variants={fadeUp}
                onClick={() => setOpen(g.id)}
                data-testid={`game-card-${g.id}`}
                className="group flex flex-col items-start rounded-2xl glass p-7 text-left transition-all duration-300 hover:-translate-y-1.5 hover:border-gold-light/50"
              >
                <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-gold-light to-gold-primary text-ink-900 transition-transform group-hover:scale-110">
                  <Icon className="h-7 w-7" />
                </span>
                <span className="mb-2 rounded-full border border-white/15 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-gold-light">{g.tag}</span>
                <h3 className="font-display text-2xl font-medium text-white">{g.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">{g.desc}</p>
                <span className="mt-5 text-sm font-medium text-gold-light">{s.playNow}</span>
              </motion.button>
            );
          })}
        </motion.div>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-testid="game-modal" className="max-w-xl border border-gold-light/20 bg-ink-800 text-white [&>button]:text-white/60 [&>button]:hover:text-gold-light">
          {current && (
            <>
              <div className="mb-2 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-gold-light to-gold-primary text-ink-900">
                  <CurrentIcon className="h-5 w-5" />
                </span>
                <h3 className="font-display text-2xl text-white">{current.title}</h3>
              </div>
              <div className="mt-2">
                <CurrentComp />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
